import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { InvoiceStats } from "@/components/invoices/InvoiceStats";
import { XpandPointInvoiceFilters } from "@/components/xpandpoint/XpandPointInvoiceFilters";
import { InvoiceTableSkeleton } from "@/components/invoices/InvoiceTableSkeleton";
import { InvoiceDetailsModal } from "@/components/invoices/InvoiceDetailsModal";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronRight,
  Eye,
  Download,
  Calendar,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  ChevronsLeft,
  ChevronLeft,
  ChevronsRight,
  FileText,
} from "lucide-react";
import { downloadResponseFile } from "@/utils/common.utils";
import type { InvoiceData } from "@/utils/pdfGenerator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PaginationControls } from "@/components/common/PaginationControl";
import { InvoiceApi, InvoiceVO } from "@/api/invoice.api";
import { format } from "date-fns";
import {
  startOfDay,
  endOfDay,
  addDays,
  startOfMonth,
  subMonths,
  endOfMonth,
} from "date-fns";
import { ProgramApi } from "@/api/program.api";
import { TransferApi } from "@/api/transfer.api";

interface TransformedInvoice {
  id: string;
  merchant: string;
  program: string;
  fullMerchantName?: string;
  fullProgramName?: string;
  issueDate: string;
  dueDate: string;
  createdDate: string;
  amountAfterTaxes: number;
  currencyCode: string;
  status: string;
  memberName: string;
  memberId: string;
  memberEmail: string;
  merchantAddress: string;
  merchantEmail: string;
  paymentMethod: string;
  subtotal: number;
  taxRate: number;
  tax: number;
  total: number;
  points: number;
  startDate: string | null;
  endDate: string | null;
  transactions: Array<{
    id: string;
    date: string;
    description: string;
    points: number;
    amount: number;
  }>;
}

const XpandPointInvoices = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<{
    status?: string;
    programCode?: string;
    transferPartnerCode?: string;
    id?: string;
    startDate?: string;
    endDate?: string;
  }>({});
  const [dateRange, setDateRange] = useState("none");
  const [sortBy, setSortBy] = useState({
    field: "createdDate",
    direction: "desc",
  });
  const [selectedInvoice, setSelectedInvoice] =
    useState<TransformedInvoice | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [invoices, setInvoices] = useState<TransformedInvoice[]>([]);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [invoiceStats, setInvoiceStats] = useState({
    totalInvoices: 0,
    totalPaid: "$0.00",
    pendingAmount: "$0.00",
    overdueAmount: "$0.00",
    averagePaymentTime: 0,
    totalPaidInvoices: 0,
    totalPendingInvoices: 0,
    totalValueOfAllInvoices: "0",
  });
  const [programs, setPrograms] = useState<any[]>([]);
  const [transferPartners, setTransferPartners] = useState<any[]>([]);
  const [resetKey, setResetKey] = useState(0);

  // Helper function for currency formatting - truncates to 2 decimal places without rounding
  const formatCurrency = (value: number, currencyCode: string = "USD") => {
    // Truncate to 2 decimal places without rounding
    const truncated = Math.floor(value * 100) / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(truncated);
  };

  // Helper function to format amount with currency code
  const formatAmountWithCode = (value: number, currencyCode: string = "USD") => {
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return `${formatted} ${currencyCode || "USD"}`.trim();
  };

  // Helper function for number formatting
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Helper function for date formatting
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return format(new Date(dateString), "MMM dd, yyyy");
  };

  // Helper to get start and end in millis for a given range
  const getDateRangeMillis = (range) => {
    const now = new Date();
    let start, end;
    switch (range) {
      case "30days":
        start = startOfDay(addDays(now, -29));
        end = endOfDay(now);
        break;
      case "7days":
        start = startOfDay(addDays(now, -6));
        end = endOfDay(now);
        break;
      case "today":
        start = startOfDay(now);
        end = endOfDay(now);
        break;
      case "yesterday":
        start = startOfDay(addDays(now, -1));
        end = endOfDay(addDays(now, -1));
        break;
      case "thisMonth":
        start = startOfMonth(now);
        end = endOfDay(now);
        break;
      case "lastMonth":
        start = startOfMonth(subMonths(now, 1));
        end = endOfMonth(subMonths(now, 1));
        break;
      case "none":
        start = null;
        end = null;
        break;
      default:
        start = null;
        end = null;
    }
    return {
      startDateInMillis:
        range === "none" ? null : start ? start.getTime() : null,
      endDateInMillis: range === "none" ? null : end ? end.getTime() : null,
    };
  };

  const fetchInvoices = async () => {
    try {
      setIsLoading(true);
      let startDateInMillis = null;
      let endDateInMillis = null;
      if (filters.startDate && filters.endDate) {
        startDateInMillis = startOfDay(new Date(filters.startDate)).getTime();
        endDateInMillis = endOfDay(new Date(filters.endDate)).getTime();
      } else {
        const range = getDateRangeMillis(dateRange);
        startDateInMillis = range.startDateInMillis;
        endDateInMillis = range.endDateInMillis;
      }

      console.log(filters.startDate, filters.endDate, "startDate");
      const response = await InvoiceApi.getInvoicesFromDB({
        invoiceType: "transfer",
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        sortBy: sortBy.field,
        sortOrder: sortBy.direction as "asc" | "desc",
        status:
          filters.status && filters.status !== "all"
            ? filters.status
            : undefined,
        programCode:
          filters.programCode && filters.programCode !== "all"
            ? filters.programCode
            : undefined,
        transferPartnerCode:
          filters.transferPartnerCode && filters.transferPartnerCode !== "all"
            ? filters.transferPartnerCode
            : undefined,
        id:
          filters.id && String(filters.id).trim().length > 0
            ? filters.id
            : undefined,
        startDateInMillis,
        endDateInMillis,
      });

      if (response.data) {
        const transformedInvoices: TransformedInvoice[] =
          response.data.invoiceVOs.map((invoice) => {
            const formatText = (text: string) => {
              if (!text) return "N/A";
              const words = text.split(" ");
              if (words.length > 2) {
                return `${words.slice(0, 2).join(" ")}...`;
              }
              return text;
            };
            const transactions = invoice.transferTransactionVOS || [];
            const points = invoice.points || 0;

            // Calculate subtotal by summing transaction amounts and round to 2 decimal places
            const transactionAmounts = transactions.map(
              (tx) =>
                tx.equivalentToSidePointsAmtAfterAdjustingLoyyalCommision || 0
            );
            const calculatedSubtotal =
              Math.round(
                transactionAmounts.reduce((sum, amount) => sum + amount, 0) *
                  100
              ) / 100;

            // Determine currency code: use program currency from invoice.program.currencyVO
            // Priority: 
            // 1. invoice.program.currencyVO.iso3CurrencyCode (for program invoices)
            // 2. invoice.transferPartnerVO.currencyVO.iso3CurrencyCode (for transfer partner invoices)
            // 3. First transaction's programVO.currencyVO (from transferTransactionVOS)
            // 4. Merchant currency as fallback
            const programCurrency =
              invoice?.program?.currencyVO?.iso3CurrencyCode ||
              invoice?.transferPartnerVO?.currencyVO?.iso3CurrencyCode ||
              invoice?.transferTransactionVOS?.[0]?.programVO?.currencyVO?.iso3CurrencyCode ||
              invoice?.transferTransactionVOS?.[0]?.programCurrencyVO?.iso3CurrencyCode ||
              null;
            const merchantCurrency =
              invoice?.merchant?.currencyVO?.iso3CurrencyCode || null;
            const currencyCode = programCurrency || merchantCurrency || "USD";

            return {
              id: invoice.id.toString(),
              merchant: formatText(invoice.from),
              program: formatText(invoice.to),
              fullMerchantName: invoice.from, // Store full merchant name for hover
              fullProgramName: invoice.to, // Store full program name for hover
              issueDate: invoice.createdDate || "N/A",
              dueDate: invoice.dueDate || "N/A",
              createdDate: invoice.createdDate || "N/A",
              amountAfterTaxes:
                invoice.amountAfterTaxes ?? invoice.amount ?? 0,
              currencyCode,
              status: (invoice.status || "PENDING").toLowerCase(),
              memberName: invoice.merchantTransactionVOS?.[0]?.memberVO
                ? `${
                    invoice.merchantTransactionVOS[0].memberVO.firstName || ""
                  } ${
                    invoice.merchantTransactionVOS[0].memberVO.lastName || ""
                  }`.trim() || "N/A"
                : "N/A",
              memberId:
                invoice.merchantTransactionVOS?.[0]?.memberVO
                  ?.programMemberId || "N/A",
              memberEmail:
                invoice.merchantTransactionVOS?.[0]?.memberVO
                  ?.personalEmailAddress || "N/A",
              merchantAddress: invoice.merchant?.businessLocation || "N/A",
              merchantEmail: invoice.merchant?.merchantEmail || "N/A",
              paymentMethod: "N/A",
              subtotal: calculatedSubtotal,
              taxRate: invoice.taxPercentage || 0,
              tax: (invoice.amountAfterTaxes || 0) - calculatedSubtotal,
              total: invoice.amountAfterTaxes || 0,
              points,
              startDate: invoice.startDate || null,
              endDate: invoice.endDate || null,
              transactions: transactions.map((tx) => ({
                id: tx.id != null ? String(tx.id) : "N/A",
                date: tx.transactionDate || "N/A",
                description: `${
                  tx.programVO?.programName || "N/A"
                } - Transfer to ${tx.transferPartnerVO?.partnerName || "N/A"}`,
                // Points column:
                // - If this invoice is for a program (invoice.program present), use programPoints
                // - If this invoice is for a transfer partner (invoice.transferPartnerVO present), use transferPartnerPoints
                points: invoice.program
                  ? tx.programPoints || 0
                  : invoice.transferPartnerVO
                  ? tx.transferPartnerPoints || 0
                  : tx.transferPartnerPoints || tx.programPoints || 0,
                amount:
                  tx.equivalentToSidePointsAmtAfterAdjustingLoyyalCommision ||
                  0,
              })),
            };
          });
        setInvoices(transformedInvoices);
        setTotalInvoices(Number(response.data.totalNumberOfInvoices) || 0);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to fetch invoices");
    } finally {
      setIsLoading(false);
    }
  };

  const totalPages = Math.ceil(totalInvoices / rowsPerPage); // Calculate totalPages here

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, rowsPerPage, sortBy, filters, dateRange]);

  useEffect(() => {
    const fetchInvoiceStats = async () => {
      try {
        const response = await fetch(
          `${
            import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
          }/invoice/getInvoiceStatisticsFromDB`,
          {
            method: "POST",
            headers: {
              accept: "application/json, text/plain, */*",
              "content-type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ invoiceType: "transfer" }),
          }
        );
        const data = await response.json();
        if (data) {
          const currencyCode = data.currencyVO?.iso3CurrencyCode || "";
          const formatStatAmount = (value: number) => {
            const truncated = Math.floor((Number(value) || 0) * 100) / 100;
            const formatted = truncated.toLocaleString("en-US", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            });
            return currencyCode
              ? `${formatted} ${currencyCode}`
              : formatted;
          };
          setInvoiceStats({
            totalInvoices: data.totalNumberOfInvoices || 0,
            totalPaid: formatStatAmount(data.totalAmountPaid || 0),
            pendingAmount: formatStatAmount(data.totalAmountPending || 0),
            overdueAmount: formatStatAmount(0), // Not provided in API, set to 0
            averagePaymentTime: 0, // Not provided in API, set to 0
            totalPaidInvoices: data.totalNumberOfPaidInvoices || 0,
            totalPendingInvoices: data.totalNumberOfPendingInvoices || 0,
            totalValueOfAllInvoices: formatStatAmount(
              data.totalValueOfAllInvoices || 0,
            ),
          });
        }
      } catch (error) {
        console.error("Error fetching invoice statistics:", error);
        toast.error("Failed to fetch invoice statistics");
      }
    };
    fetchInvoiceStats();
  }, []);

  useEffect(() => {
    // Fetch programs and transfer partners for filter dropdowns
    const fetchProgramsAndPartners = async () => {
      try {
        const [programRes, partnerRes] = await Promise.all([
          ProgramApi.getProgramsFromDB({
            pageNumber: 0,
            pageSize: 100,
            sortOrder: "desc",
            sortBy: "createdDate",
            programCode: null,
            programName: null,
            programType: "TRANSFER",
          }),
          TransferApi.getTransferPartnersFromDB({
            pageNumber: 0,
            pageSize: 100,
            sortOrder: "desc",
            sortBy: "createdDate",
          }),
        ]);
        setPrograms(
          Array.isArray(programRes.data?.programVOs)
            ? programRes.data.programVOs
            : []
        );
        setTransferPartners(
          Array.isArray(partnerRes.data?.transferPartnerVOList)
            ? partnerRes.data.transferPartnerVOList
            : []
        );
      } catch (e) {
        setPrograms([]);
        setTransferPartners([]);
      }
    };
    fetchProgramsAndPartners();
  }, []);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setFilters((f) => ({ ...f, id: term }));
    setCurrentPage(0);
  };

  const handleFilterChange = (newFilters: any) => {
    const merged = { ...filters, ...newFilters };
    Object.keys(merged).forEach((key) => {
      if (merged[key] === "all") delete merged[key];
    });
    setFilters(merged);
    setCurrentPage(0);
  };

  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
  };

  const handleReset = () => {
    setSearchTerm("");
    setFilters({});
    setDateRange("none");
    setSortBy({ field: "createdDate", direction: "desc" });
    setCurrentPage(0);
    setResetKey((prev) => prev + 1); // Trigger UI reset in filters
    toast.success("All filters have been reset");
  };

  const handleSort = (field: string) => {
    if (sortBy.field === field) {
      setSortBy({
        field,
        direction: sortBy.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSortBy({ field, direction: "asc" });
    }
  };

  const handleViewDetails = (invoice: TransformedInvoice) => {
    setSelectedInvoice(invoice);
    setIsDetailsModalOpen(true);
  };

  const handleDownloadInvoice = async (invoiceId: string) => {
    const cleanInvoiceId = invoiceId.replace("#", "");
    const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
    try {
      const response = await fetch(
        `${API_BASE}/invoice/v2/generateInvoicePDF?invoiceId=${cleanInvoiceId}`,
        {
          method: "GET",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to download PDF");
      }
      const filename = await downloadResponseFile(
        response,
        `invoice-${cleanInvoiceId}.pdf`
      );
      toast.success(`Invoice ${filename} downloaded successfully!`);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error(`Failed to download invoice ${invoiceId}`);
    }
  };

  const handleDownloadInvoiceCSV = async (invoiceId: string) => {
    const cleanInvoiceId = invoiceId.replace("#", "");
    const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
    try {
      const response = await fetch(
        `${API_BASE}/invoice/getTransactionsFromInvoiceInCSVFormat?invoiceId=${cleanInvoiceId}`,
        {
          method: "GET",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to download CSV");
      }
      const filename = await downloadResponseFile(
        response,
        `invoice-${cleanInvoiceId}.csv`
      );
      toast.success(`Invoice CSV ${filename} downloaded successfully!`);
    } catch (error) {
      console.error("Error downloading invoice CSV:", error);
      toast.error(`Failed to download invoice CSV ${invoiceId}`);
    }
  };

  const handleMarkAsPaid = async (invoiceId: string) => {
    try {
      // Remove # from invoice ID if present
      const cleanInvoiceId = invoiceId.replace("#", "");
      const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
      const url = `${API_BASE}/invoice/updateInvoiceStatus?invoiceId=${cleanInvoiceId}&status=PAID`;
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
      });
      if (response.ok) {
        toast.success(`Invoice ${invoiceId} marked as paid successfully!`);
        fetchInvoices();
      } else {
        const errorText = await response.text();
        toast.error(
          `Failed to mark invoice ${invoiceId} as paid: ${errorText}`
        );
      }
    } catch (error) {
      console.error("Error marking invoice as paid:", error);
      toast.error(`Failed to mark invoice ${invoiceId} as paid`);
    }
  };

  const SortableHeader = ({
    field,
    children,
  }: {
    field: string;
    children: React.ReactNode;
  }) => {
    const isSorted = sortBy.field === field;
    const direction = sortBy.direction;

    return (
      <div
        className="flex items-center cursor-pointer"
        onClick={() => handleSort(field)}
      >
        {children}
        <span className="ml-1">
          {isSorted && direction === "asc" && <ArrowUp className="h-4 w-4" />}
          {isSorted && direction === "desc" && (
            <ArrowDown className="h-4 w-4" />
          )}
          {!isSorted && <ChevronRight className="h-4 w-4 opacity-30" />}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Invoices</h1>
        <p className="text-gray-500 mt-1">
          Manage and view all invoices for your transfer programs/partners
        </p>
      </div>

      {/* Invoice Stats */}
      <InvoiceStats isLoading={isLoading} stats={invoiceStats} />

      {/* Invoice Filters */}
      <XpandPointInvoiceFilters
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        onDateRangeChange={handleDateRangeChange}
        onReset={handleReset}
        programs={programs}
        transferPartners={transferPartners}
        resetKey={resetKey}
      />

      {/* Invoices Table */}
      {isLoading ? (
        <InvoiceTableSkeleton rowCount={rowsPerPage} />
      ) : (
        <DataTable
          title="All Invoices"
          data={invoices}
          columns={[
            {
              header: "Invoice ID",
              accessorKey: "id",
            },
            {
              header: "Invoice From",
              accessorKey: "merchant",
              cell: (row) => {
                const fullText = row.fullMerchantName || row.merchant;
                return <span title={fullText}>{row.merchant}</span>;
              },
            },
            {
              header: "Invoice To",
              accessorKey: "program",
              cell: (row) => {
                const fullText = row.fullProgramName || row.program;
                return <span title={fullText}>{row.program}</span>;
              },
            },
            {
              header: "Create Date",
              accessorKey: "createdDate",
              cell: (row) => (
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-gray-500" />
                  <span>{formatDate(row.createdDate)}</span>
                </div>
              ),
            },
            {
              header: "Points Exchanged",
              accessorKey: "points",
              cell: (row) => (
                <span className="font-medium">{formatNumber(row.points)}</span>
              ),
            },
            {
              header: <SortableHeader field="dueDate">Due Date</SortableHeader>,
              accessorKey: "dueDate",
              cell: (row) => (
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-gray-500" />
                  <span>{formatDate(row.dueDate)}</span>
                </div>
              ),
            },
            {
              header: (
                <SortableHeader field="amountAfterTaxes">Amount</SortableHeader>
              ),
              accessorKey: "amountAfterTaxes",
              cell: (row) => (
                <span className="font-medium">
                  {formatAmountWithCode(
                    row.amountAfterTaxes,
                    row.currencyCode
                  )}
                </span>
              ),
            },
            {
              header: <SortableHeader field="status">Status</SortableHeader>,
              accessorKey: "status",
              cell: (row) => {
                const statusStyles = {
                  paid: "bg-green-100 text-green-800 hover:bg-green-100",
                  pending: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
                  overdue: "bg-red-100 text-red-800 hover:bg-red-100",
                };

                return (
                  <Badge
                    variant="outline"
                    className={
                      statusStyles[row.status as keyof typeof statusStyles]
                    }
                  >
                    {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                  </Badge>
                );
              },
            },
            {
              header: "Actions",
              id: "actions",
              cell: (row) => (
                <div className="flex justify-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white">
                      <DropdownMenuItem onClick={() => handleViewDetails(row)}>
                        <Eye className="mr-2 h-4 w-4" />
                        <span>View Details</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDownloadInvoice(row.id)}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        <span>Download invoice PDF</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDownloadInvoiceCSV(row.id)}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        <span>Download invoice CSV</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          row.status !== "paid" && handleMarkAsPaid(row.id)
                        }
                        disabled={row.status === "paid"}
                      >
                        <span className="mr-2 h-4 w-4">✓</span>
                        <span>Mark as Paid</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Invoice Details Modal */}
      <InvoiceDetailsModal
        open={isDetailsModalOpen}
        onOpenChange={setIsDetailsModalOpen}
        invoice={selectedInvoice}
      />

      {/* Pagination controls */}
      {!isLoading && totalInvoices > 0 && (
        <div className="mt-4 w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Rows per page:</span>
              <Select
                value={rowsPerPage.toString()}
                onValueChange={(value) => {
                  setRowsPerPage(Number(value));
                  setCurrentPage(0);
                }}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue placeholder={rowsPerPage.toString()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <button
                      className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                        currentPage === 0
                          ? "pointer-events-none opacity-50"
                          : ""
                      }`}
                      onClick={() => setCurrentPage(0)}
                      disabled={currentPage === 0}
                      aria-label="First page"
                      type="button"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </button>
                  </PaginationItem>
                  {currentPage > 0 ? (
                    <PaginationPrevious
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(0, prev - 1))
                      }
                      className="gap-0 px-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </PaginationPrevious>
                  ) : (
                    <PaginationPrevious
                      onClick={() => {}}
                      className="pointer-events-none opacity-50 gap-0 px-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </PaginationPrevious>
                  )}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNumber =
                      currentPage <= 2 ? i : currentPage + i - 2;
                    if (pageNumber >= totalPages) return null;
                    return (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink
                          onClick={() => setCurrentPage(pageNumber)}
                          isActive={currentPage === pageNumber}
                        >
                          {pageNumber + 1}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  {currentPage < totalPages - 1 ? (
                    <PaginationNext
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(totalPages - 1, prev + 1)
                        )
                      }
                      className="gap-0 px-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </PaginationNext>
                  ) : (
                    <PaginationNext
                      onClick={() => {}}
                      className="pointer-events-none opacity-50 gap-0 px-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </PaginationNext>
                  )}
                  <PaginationItem>
                    <button
                      className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                        currentPage === totalPages - 1
                          ? "pointer-events-none opacity-50"
                          : ""
                      }`}
                      onClick={() => setCurrentPage(totalPages - 1)}
                      disabled={currentPage === totalPages - 1}
                      aria-label="Last page"
                      type="button"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </button>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
            <div className="w-[160px]" /> {/* Spacer to balance layout */}
          </div>
        </div>
      )}
    </div>
  );
};

export default XpandPointInvoices;
